import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/lib/supabase';

export type OnboardingStepId =
  | 'configure-business'   // name + phone + email + address + opening_hours
  | 'configure-services'   // categories >= 1 AND services >= 1
  | 'create-professional'  // professionals >= 1 AND professional_services >= 1
  | 'create-client'        // clients count >= 1
  | 'create-appointment'   // appointments count >= 1
  // Industry-specific:
  | 'group-classes'
  | 'patient-forms'
  | 'patient-file';

interface OnboardingStepDefinition {
  id: OnboardingStepId;
  label: string;
  shortLabel: string;
  description: string;
  route: string;
  ctaLabel: string;
}

export interface OnboardingStep extends OnboardingStepDefinition {
  isComplete: boolean;
  isAccessible: boolean;
  percent: number;
}

export interface SubItemStatus {
  salonName: boolean;
  salonPhone: boolean;
  salonEmail: boolean;
  salonAddress: boolean;
  salonLogo: boolean;
  /** True only after the user explicitly saves hours (opening_hours_confirmed flag) */
  salonHours: boolean;
  hasCategories: boolean;
  hasServices: boolean;
  /** True when at least one active professional exists */
  hasProfessionals: boolean;
  /** True when at least one professional_services link exists */
  hasProfessionalServices: boolean;
  /** True when at least one professional has commission_percentage > 0 */
  hasProfessionalCommission: boolean;
  /** True when at least one client exists */
  hasClients: boolean;
  hasAppointments: boolean;
}

export interface OnboardingProgress {
  salonId: string | null;
  steps: OnboardingStep[];
  completedCount: number;
  totalCount: number;
  percentage: number;
  isComplete: boolean;
  isRequired: boolean;
  isActive: boolean;
  /** True after the user clicks "Empezar" in the welcome modal (persisted in DB) */
  isStarted: boolean;
  loading: boolean;
  currentStep: OnboardingStep | null;
  completedAt: string | null;
  shouldFinalize: boolean;
  needsRequirementBackfill: boolean;
  subItemStatus: SubItemStatus;
}

interface SalonSettings {
  onboarding_v2_required?: boolean;
  onboarding_v2_completed_at?: string | null;
  /** Set to true when the user clicks "Empezar" in the welcome modal */
  onboarding_v2_started?: boolean;
  /** Set to true when the user explicitly saves the hours settings form */
  opening_hours_confirmed?: boolean;
  online_booking?: {
    enabled?: boolean;
  };
  [key: string]: unknown;
}

interface SalonRow {
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  email?: string | null;
  logo_url?: string | null;
  opening_hours?: Record<string, unknown> | null;
  settings?: SalonSettings | null;
  created_at?: string | null;
}

interface OnboardingStatus {
  hasBusinessData: boolean;
  hasOpeningHours: boolean;
  hasCategories: boolean;
  hasServices: boolean;
  hasProfessionals: boolean;
  hasProfessionalsCreated: boolean;
  hasProfessionalServiceLinks: boolean;
  hasProfessionalCommission: boolean;
  hasClients: boolean;
  hasAppointments: boolean;
  hasGroupClasses: boolean;
  hasPatientForms: boolean;
  hasPatientFile: boolean;
  isRequired: boolean;
  isStarted: boolean;
  completedAt: string | null;
  needsRequirementBackfill: boolean;
  // Granular sub-item data
  salonName: string | null;
  salonPhone: string | null;
  salonEmail: string | null;
  salonAddress: string | null;
  salonLogoUrl: string | null;
}

const ONBOARDING_V2_ROLLOUT_AT = Date.parse('2026-03-23T00:00:00.000Z');

// ── Base steps (all industries) ───────────────────────────────────────────────
const BASE_STEPS: OnboardingStepDefinition[] = [
  {
    id: 'configure-business',
    label: 'Configurar negocio',
    shortLabel: 'Negocio',
    description: 'Ingresa los datos básicos de tu negocio y define tus horarios de atención.',
    route: '/settings?section=salon',
    ctaLabel: 'Configurar negocio',
  },
  {
    id: 'configure-services',
    label: 'Configurar servicios',
    shortLabel: 'Servicios',
    description: 'Crea las categorías y servicios que ofrecerás a tus clientes.',
    route: '/services',
    ctaLabel: 'Ir a Servicios',
  },
  {
    id: 'create-professional',
    label: 'Crear profesional',
    shortLabel: 'Profesional',
    description: 'Agrega un profesional, asígnale servicios y define su comisión.',
    route: '/professionals',
    ctaLabel: 'Ir a Profesionales',
  },
  {
    id: 'create-client',
    label: 'Crear cliente',
    shortLabel: 'Cliente',
    description: 'Registra tu primer cliente en el sistema.',
    route: '/clients',
    ctaLabel: 'Ir a Clientes',
  },
  {
    id: 'create-appointment',
    label: 'Crear cita',
    shortLabel: 'Cita',
    description: 'Agenda tu primera cita. Tu negocio está listo para operar.',
    route: '/appointments',
    ctaLabel: 'Ir a Agenda',
  },
];

// ── Industry-specific extra steps ─────────────────────────────────────────────
const STEP_GROUP_CLASSES: OnboardingStepDefinition = {
  id: 'group-classes',
  label: 'Clases grupales',
  shortLabel: 'Clases',
  description: 'Crea tu primera clase grupal con cupo y horario para que los clientes se inscriban.',
  route: '/group-classes',
  ctaLabel: 'Crear primera clase',
};

const STEP_PATIENT_FORMS: OnboardingStepDefinition = {
  id: 'patient-forms',
  label: 'Formularios del paciente',
  shortLabel: 'Formularios',
  description: 'Configura al menos un consentimiento informado para tus pacientes.',
  route: '/clients',
  ctaLabel: 'Ir a clientes',
};

const STEP_PATIENT_FILE: OnboardingStepDefinition = {
  id: 'patient-file',
  label: 'Ficha del paciente',
  shortLabel: 'Ficha',
  description: 'Registra tu primer paciente para activar la ficha clínica de tu industria.',
  route: '/clients',
  ctaLabel: 'Registrar primer paciente',
};

const INDUSTRY_EXTRA_STEPS: Record<string, OnboardingStepDefinition[]> = {
  fitness:    [STEP_GROUP_CLASSES],
  spa:        [STEP_GROUP_CLASSES],
  psychology: [STEP_PATIENT_FORMS],
  nutrition:  [STEP_PATIENT_FORMS],
  medical:    [STEP_PATIENT_FORMS],
  veterinary: [STEP_PATIENT_FILE],
  dental:     [STEP_PATIENT_FILE],
};

export function getStepsForIndustry(industryType: string | null | undefined): OnboardingStepDefinition[] {
  const extra = industryType ? (INDUSTRY_EXTRA_STEPS[industryType] ?? []) : [];
  return [...BASE_STEPS, ...extra];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasConfiguredOpeningHours(openingHours: SalonRow['opening_hours']): boolean {
  if (!isRecord(openingHours)) return false;
  return Object.values(openingHours).some((day) => {
    if (!isRecord(day)) return false;
    const open = day.open;
    return typeof open === 'string' ? open.trim().length > 0 : open !== null && open !== undefined;
  });
}

function getRouteSearchParams(route: string) {
  return new URL(route, 'https://mestly.app');
}

export function matchesOnboardingStepRoute(
  step: Pick<OnboardingStep, 'route'>,
  pathname: string,
  search: string,
): boolean {
  const target = getRouteSearchParams(step.route);
  const currentSearchParams = new URLSearchParams(search);
  if (target.pathname !== pathname) return false;
  for (const [key, value] of target.searchParams.entries()) {
    if (currentSearchParams.get(key) !== value) return false;
  }
  return true;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useOnboardingProgress(): OnboardingProgress {
  const { user } = useAuth();
  const { currentOrganization } = useTenant();
  const organizationId = currentOrganization?.id ?? user?.organization_id ?? null;
  const salonId = user?.salon_id ?? null;
  const industryType = currentOrganization?.industry_type ?? null;

  const stepDefinitions = getStepsForIndustry(industryType);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding-progress', organizationId, salonId, industryType],
    queryFn: async (): Promise<OnboardingStatus | null> => {
      if (!organizationId || !salonId) return null;

      const needsGroupClasses = industryType === 'fitness' || industryType === 'spa';
      const needsPatientForms = industryType === 'psychology' || industryType === 'nutrition' || industryType === 'medical';
      const needsPatientFile  = industryType === 'veterinary' || industryType === 'dental';

      const baseQueries = Promise.all([
        supabase.from('salons').select('name, phone, address, email, logo_url, opening_hours, settings, created_at').eq('id', salonId).maybeSingle(),
        supabase.from('service_categories').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId),
        supabase.from('services').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId).eq('is_active', true),
        supabase.from('professionals').select('id, commission_percentage').eq('organization_id', organizationId).eq('is_active', true),
        supabase.from('clients').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId),
        supabase.from('appointments').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId),
      ]);

      const [salonRes, categoriesRes, servicesRes, professionalsRes, clientsRes, appointmentsRes] = await baseQueries;

      if (salonRes.error) throw salonRes.error;
      if (categoriesRes.error) throw categoriesRes.error;
      if (servicesRes.error) throw servicesRes.error;
      if (professionalsRes.error) throw professionalsRes.error;
      if (clientsRes.error) throw clientsRes.error;
      if (appointmentsRes.error) throw appointmentsRes.error;

      const salon = (salonRes.data as SalonRow | null) ?? null;
      const settings = isRecord(salon?.settings) ? salon.settings as SalonSettings : {};
      const professionalsData = professionalsRes.data ?? [];
      const professionalIds = professionalsData
        .map((p) => p.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
      const hasAnyCommission = professionalsData.some(
        (p) => typeof p.commission_percentage === 'number' && p.commission_percentage > 0,
      );

      let professionalServiceLinks = 0;
      if (professionalIds.length > 0) {
        const { count, error } = await supabase
          .from('professional_services')
          .select('professional_id', { count: 'exact', head: true })
          .in('professional_id', professionalIds);
        if (error) throw error;
        professionalServiceLinks = count ?? 0;
      }

      // Industry-specific checks (run in parallel)
      const [groupClassesCount, patientFormsCount, patientFileCount] = await Promise.all([
        needsGroupClasses
          ? supabase.from('group_classes').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId).eq('is_active', true)
          : Promise.resolve({ count: 0, error: null }),
        needsPatientForms
          ? supabase.from('consent_forms').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId).eq('is_active', true)
          : Promise.resolve({ count: 0, error: null }),
        needsPatientFile
          ? supabase.from('clients').select('id', { count: 'exact', head: true }).eq('organization_id', organizationId)
          : Promise.resolve({ count: 0, error: null }),
      ]);

      const completedAt = typeof settings.onboarding_v2_completed_at === 'string'
        ? settings.onboarding_v2_completed_at
        : null;
      const createdAt = typeof salon?.created_at === 'string' ? Date.parse(salon.created_at) : Number.NaN;
      const hasExplicitRequirement = settings.onboarding_v2_required === true;
      const hasRequirementKey = typeof settings.onboarding_v2_required === 'boolean';
      const shouldFallbackToRollout = !hasRequirementKey
        && !completedAt
        && Number.isFinite(createdAt)
        && createdAt >= ONBOARDING_V2_ROLLOUT_AT;

      const openingHoursConfirmed = settings.opening_hours_confirmed === true;
      const isStarted = settings.onboarding_v2_started === true;

      return {
        hasBusinessData: Boolean(
          typeof salon?.name === 'string' && salon.name.trim()
          && typeof salon?.phone === 'string' && salon.phone.trim()
          && typeof salon?.address === 'string' && salon.address.trim()
          && typeof salon?.email === 'string' && salon.email.trim(),
        ),
        // Only consider hours done when the user explicitly saved the hours form
        hasOpeningHours: openingHoursConfirmed,
        hasCategories: (categoriesRes.count ?? 0) >= 1,
        hasServices: (servicesRes.count ?? 0) >= 1,
        hasProfessionals: professionalIds.length >= 1 && professionalServiceLinks >= 1,
        hasProfessionalsCreated: professionalIds.length >= 1,
        hasProfessionalServiceLinks: professionalServiceLinks >= 1,
        hasProfessionalCommission: hasAnyCommission,
        hasClients: (clientsRes.count ?? 0) >= 1,
        hasAppointments: (appointmentsRes.count ?? 0) >= 1,
        hasGroupClasses: (groupClassesCount.count ?? 0) >= 1,
        hasPatientForms: (patientFormsCount.count ?? 0) >= 1,
        hasPatientFile: (patientFileCount.count ?? 0) >= 1,
        isRequired: hasExplicitRequirement || shouldFallbackToRollout,
        isStarted,
        completedAt,
        needsRequirementBackfill: shouldFallbackToRollout,
        // Granular sub-item data
        salonName: typeof salon?.name === 'string' && salon.name.trim() ? salon.name : null,
        salonPhone: typeof salon?.phone === 'string' && salon.phone.trim() ? salon.phone : null,
        salonEmail: typeof salon?.email === 'string' && salon.email.trim() ? salon.email : null,
        salonAddress: typeof salon?.address === 'string' && salon.address.trim() ? salon.address : null,
        salonLogoUrl: typeof salon?.logo_url === 'string' && salon.logo_url.trim() ? salon.logo_url : null,
      };
    },
    enabled: Boolean(salonId),
    staleTime: 10000,
  });

  const completionMap: Partial<Record<OnboardingStepId, boolean>> = {
    'configure-business': Boolean(data?.hasBusinessData && data?.hasOpeningHours),
    'configure-services': Boolean(data?.hasCategories && data?.hasServices),
    'create-professional': Boolean(data?.hasProfessionals),
    'create-client': Boolean(data?.hasClients),
    'create-appointment': Boolean(data?.hasAppointments),
    'group-classes':  data?.hasGroupClasses ?? false,
    'patient-forms':  data?.hasPatientForms ?? false,
    'patient-file':   data?.hasPatientFile ?? false,
  };

  const firstIncompleteIndex = stepDefinitions.findIndex((step) => !completionMap[step.id]);
  const totalCount = stepDefinitions.length;

  const steps: OnboardingStep[] = stepDefinitions.map((step, index) => {
    const isComplete = completionMap[step.id] ?? false;
    const isAccessible = firstIncompleteIndex === -1 ? true : isComplete || index <= firstIncompleteIndex;
    return {
      ...step,
      isComplete,
      isAccessible,
      percent: Math.round(((index + 1) / totalCount) * 100),
    };
  });

  const completedCount = steps.filter((step) => step.isComplete).length;
  const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const currentStep = steps.find((step) => !step.isComplete) ?? null;
  const completedAt = data?.completedAt ?? null;
  const isRequired = data?.isRequired === true;
  const isActive = Boolean(salonId) && isRequired && !completedAt;
  const isComplete = completedAt !== null || (completedCount === totalCount && totalCount > 0);

  const subItemStatus: SubItemStatus = {
    salonName: Boolean(data?.salonName),
    salonPhone: Boolean(data?.salonPhone),
    salonEmail: Boolean(data?.salonEmail),
    salonAddress: Boolean(data?.salonAddress),
    salonLogo: Boolean(data?.salonLogoUrl),
    salonHours: data?.hasOpeningHours ?? false,   // uses opening_hours_confirmed flag
    hasCategories: data?.hasCategories ?? false,
    hasServices: data?.hasServices ?? false,
    hasProfessionals: data?.hasProfessionalsCreated ?? false,
    hasProfessionalServices: data?.hasProfessionalServiceLinks ?? false,
    hasProfessionalCommission: data?.hasProfessionalCommission ?? false,
    hasClients: data?.hasClients ?? false,
    hasAppointments: data?.hasAppointments ?? false,
  };

  return {
    salonId,
    steps,
    completedCount,
    totalCount,
    percentage,
    isComplete,
    isRequired,
    isActive,
    isStarted: data?.isStarted ?? false,
    loading: isLoading,
    currentStep,
    completedAt,
    shouldFinalize: isActive && completedCount === totalCount && totalCount > 0,
    needsRequirementBackfill: data?.needsRequirementBackfill ?? false,
    subItemStatus,
  };
}
